import React, { useEffect, useState } from 'react';
import { Shield, Github } from 'lucide-react';
import TabNavigation, { TabType } from './components/TabNavigation';
import HomeTab from './components/HomeTab';
import InstrumentTab from './components/InstrumentTab';
import FAQTab from './components/FAQTab';
import ContactTab from './components/ContactTab';
import { surveyApi } from './services/api';
import { REPO_URL } from './constants/links';
import { SurveyAnalysisRequest, SurveyAnalysisResult, AnalysisStatus } from './types/survey';

type AppState = 'form' | 'analyzing' | 'results';

function App() {
  const [activeTab, setActiveTab] = useState<TabType>('instrument');
  const [liveTakeEnabled, setLiveTakeEnabled] = useState<boolean>(false);
  const [liveTakeReason, setLiveTakeReason] = useState<
    'missing_extra' | 'missing_api_key' | null | undefined
  >(undefined);

  useEffect(() => {
    let cancelled = false;
    surveyApi.getConfig().then((res) => {
      if (cancelled) return;
      if (res.error) {
        // Default-to-false is the desired hosted-demo behavior, but a
        // self-hoster whose backend is unreachable should at least see
        // the failure in their console.
        console.warn('getConfig failed; falling back to live_take_enabled=false:', res.error);
      }
      setLiveTakeEnabled(Boolean(res.data?.live_take_enabled));
      setLiveTakeReason(res.data?.reason);
    });
    return () => { cancelled = true; };
  }, []);
  const [appState, setAppState] = useState<AppState>('form');
  const [analysisId, setAnalysisId] = useState<string>('');
  const [analysisResult, setAnalysisResult] = useState<SurveyAnalysisResult | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string>('');

  const handleSubmitAnalysis = async (request: SurveyAnalysisRequest) => {
    setIsLoading(true);
    setError('');

    try {
      const response = await surveyApi.submitAnalysis(request);

      if (response.error) {
        setError(response.error);
        setIsLoading(false);
        return;
      }

      if (response.data) {
        setAnalysisId(response.data.analysis_id);
        setAppState('analyzing');
      }
    } catch (err) {
      setError('Failed to submit analysis. Please try again.');
    }

    setIsLoading(false);
  };

  const handleStatusUpdate = (status: AnalysisStatus) => {
    // Handle status updates if needed
    console.log('Status update:', status);
  };

  const handleAnalysisComplete = async () => {
    try {
      const response = await surveyApi.getResults(analysisId);

      if (response.error) {
        setError(response.error);
        return;
      }

      if (response.data) {
        setAnalysisResult(response.data);
        setAppState('results');
      }
    } catch (err) {
      setError('Failed to retrieve analysis results.');
    }
  };

  const handleExportReport = () => {
    if (!analysisResult) return;

    const reportData = {
      ...analysisResult,
      exported_at: new Date().toISOString(),
    };

    const blob = new Blob([JSON.stringify(reportData, null, 2)], {
      type: 'application/json',
    });

    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `survey-shield-report-${analysisResult.survey_domain}-${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleStartNew = () => {
    setAppState('form');
    setAnalysisId('');
    setAnalysisResult(null);
    setError('');
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center space-x-8">
              <div className="flex items-center space-x-3">
                <Shield className="w-8 h-8 text-primary-600" />
                <h1 className="text-xl font-bold text-gray-900">Survey Shield</h1>
                <span className="px-2 py-1 text-xs bg-primary-100 text-primary-600 rounded-full">
                  Beta
                </span>
              </div>
              <TabNavigation activeTab={activeTab} onTabChange={setActiveTab} />
            </div>
            <div className="flex items-center space-x-4">
              <a
                href={REPO_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-500 hover:text-gray-700 transition-colors"
              >
                <Github className="w-5 h-5" />
              </a>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {activeTab === 'home' && (
          <HomeTab
            liveTakeEnabled={liveTakeEnabled}
            liveTakeReason={liveTakeReason}
            appState={appState}
            analysisId={analysisId}
            analysisResult={analysisResult}
            isLoading={isLoading}
            error={error}
            onSubmitAnalysis={handleSubmitAnalysis}
            onStatusUpdate={handleStatusUpdate}
            onAnalysisComplete={handleAnalysisComplete}
            onExportReport={handleExportReport}
            onStartNew={handleStartNew}
          />
        )}

        {activeTab === 'instrument' && <InstrumentTab />}

        {activeTab === 'faq' && <FAQTab />}

        {activeTab === 'contact' && <ContactTab />}
      </main>

      {/* Footer */}
      <footer className="bg-white border-t mt-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="text-center text-sm text-gray-500">
            <p>
              Survey Shield v1.0.0 - Academic survey analysis tool
            </p>
            <p className="mt-1">
              <a href="/docs" className="hover:text-gray-700">Documentation</a>
              {' • '}
              <a href={REPO_URL} className="hover:text-gray-700">
                GitHub
              </a>
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default App;